import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/auth";
import { EnterpriseGate } from "@/components/enterprise-gate";
import { useListStaff } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Loader2, Clock, UserCheck, UserX, Calendar } from "lucide-react";
import { format } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { readJson } from "@/lib/http";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

async function fetchApi<T = unknown>(path: string, opts?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE}${path}`, { credentials: "include", ...opts });
  return readJson<T>(response);
}

export default function Attendance() {
  return (
    <EnterpriseGate
      feature="Staff Attendance Tracking"
      description="Track clock-in/clock-out times, monitor staff hours, and review attendance history. Available on the Enterprise plan."
    >
      <AttendanceContent />
    </EnterpriseGate>
  );
}

function AttendanceContent() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const today = new Date().toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedStaff, setSelectedStaff] = useState<string>("");

  const { data: staffList = [] } = useListStaff();
  const { data: status = [], isLoading: statusLoading } = useQuery({
    queryKey: ["attendance-status", today],
    queryFn: () => fetchApi("/api/attendance/status"),
    refetchInterval: 30000,
  });
  const { data: logs = [], isLoading: logsLoading } = useQuery({
    queryKey: ["attendance-logs", selectedDate],
    queryFn: () => fetchApi(`/api/attendance?date=${selectedDate}`),
  });

  const clockIn = useMutation({
    mutationFn: (staffId: number) => fetchApi("/api/attendance/clock-in", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ staffId }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance-status"] });
      queryClient.invalidateQueries({ queryKey: ["attendance-logs"] });
    },
    onError: () => toast({ title: "Failed to clock in", variant: "destructive" }),
  });

  const clockOut = useMutation({
    mutationFn: (staffId: number) => fetchApi("/api/attendance/clock-out", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ staffId }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["attendance-status"] });
      queryClient.invalidateQueries({ queryKey: ["attendance-logs"] });
    },
    onError: () => toast({ title: "Failed to clock out", variant: "destructive" }),
  });

  const activeStaff = (staffList as any[]).filter(s => s.isActive);
  const clockedInIds = new Set((status as any[]).filter(s => s.isClocked).map((s: any) => s.staffId));

  const totalHours = (logs as any[]).reduce((sum: number, r: any) => sum + (r.hoursWorked || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Attendance</h1>
        <p className="text-muted-foreground mt-1">Track staff clock-in and clock-out times.</p>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Clocked In Now</CardTitle>
            <UserCheck className="w-4 h-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              {(status as any[]).filter((s: any) => s.isClocked).length}
            </div>
            <p className="text-xs text-muted-foreground">of {activeStaff.length} active staff</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Total Hours Today</CardTitle>
            <Clock className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalHours.toFixed(1)}h</div>
            <p className="text-xs text-muted-foreground">across all staff</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Not Clocked In</CardTitle>
            <UserX className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {activeStaff.length - (status as any[]).filter((s: any) => s.isClocked).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clock In/Out panel */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" /> Clock In / Out</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {activeStaff.map((s: any) => {
              const isIn = clockedInIds.has(s.id);
              return (
                <div key={s.id} className="flex items-center gap-2 p-3 border rounded-lg bg-card min-w-[200px]">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{s.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{s.role}</p>
                  </div>
                  <Badge variant={isIn ? "default" : "secondary"} className="text-xs">
                    {isIn ? "In" : "Out"}
                  </Badge>
                  <Button
                    size="sm"
                    variant={isIn ? "outline" : "default"}
                    className="h-7 text-xs"
                    onClick={() => isIn ? clockOut.mutate(s.id) : clockIn.mutate(s.id)}
                    disabled={clockIn.isPending || clockOut.isPending}
                  >
                    {isIn ? "Clock Out" : "Clock In"}
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Attendance log */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5" /> Attendance Log</CardTitle>
          <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-40 h-8 text-sm" />
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (logs as any[]).length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No records for {selectedDate}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(logs as any[]).map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.staffName}</TableCell>
                    <TableCell className="text-sm">{format(new Date(r.clockIn), "h:mm a")}</TableCell>
                    <TableCell className="text-sm">{r.clockOut ? format(new Date(r.clockOut), "h:mm a") : "—"}</TableCell>
                    <TableCell>{r.hoursWorked != null ? `${r.hoursWorked}h` : "—"}</TableCell>
                    <TableCell>
                      <Badge variant={r.clockOut ? "secondary" : "default"}>
                        {r.clockOut ? "Completed" : "Active"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
